import {jsPDF} from "jspdf";
import {myFontLight} from "../../lib/mplus_light";
import dayjs from "dayjs";
import {formatToDate, formatToPrice, toCancelFeeCaption, toCircled} from "../convert";
import {consumptionTax} from "../tax";
import {reserveCancelDetailResponce} from "../../types/reserveCancel";
// react-i18next v12 は TFunction を再エクスポートしないため i18next から取得する
import {TFunction} from "i18next";

export const generateInvoicePdfPageContent = async (pdfDoc: jsPDF, reserveData: reserveCancelDetailResponce, pdfType: "invoice" | "receipt", t: TFunction, lang: string) => {

  // 画像のURLまたはBase64データ
  const imageUrl = '/common/images/kakuin.png';

  // 画像を取得してBase64変換（外部画像を使用する場合）
  const toBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  const kakuinImage = await toBase64(imageUrl);

  // PDFに画像を追加
  pdfDoc.addImage(kakuinImage, 'PNG', 165, 60, 20, 20); // x, y, width, height

  /**
   * 日本語フォントの設定など
   */
  pdfDoc.addFileToVFS('mplus-1p-light.ttf', myFontLight);
  pdfDoc.addFont('mplus-1p-light.ttf', 'mplusLight', 'normal');
  pdfDoc.setFont('mplusLight', 'normal');
  const leftPosition = 20;
  const leftPositionContent = leftPosition + 1;
  const themeColor = "#2C849C";

  /**
   * 枠線などの固定レイアウトを作成する
   */
  // 請求書・領収書ヘッダー
  const title = pdfType === 'invoice' ? t("請　求　書") : t("領　収　書");
  pdfDoc.setDrawColor(themeColor);
  pdfDoc.setFillColor(themeColor);
  pdfDoc.rect(leftPosition, 20, 170, 15, "F");
  pdfDoc.setFontSize(22);
  pdfDoc.setTextColor("#FFFFFF");
  pdfDoc.text(title, 82, 30);
  pdfDoc.setTextColor("#000000");

  // 発行日・伝票番号
  const bookingId = reserveData.data.bookingData.bookingId?.replace("DMO-", "") ?? "";
  const paddingZero = bookingId.padStart(6, "0");
  const slipNumber = `${paddingZero.slice(0, 3)}-${paddingZero.slice(3, 6)}-${dayjs().format("YYYYMM")}`;

  const bookingDate = formatToDate(reserveData.data.bookingData.bookingDate, t("YYYY年MM月DD日"));
  // 出発日
  const departureDate = formatToDate(reserveData.data.bookingData.departureDate, t("YYYY年MM月DD日"));

  const issueDateX = lang === 'en' ? 142 : 148;
  const orderNumberX = lang === 'en' ? 135 : 145;
  pdfDoc.setFontSize(9);
  pdfDoc.text(t("発行日")+`： ${bookingDate}`, issueDateX, 42);
  pdfDoc.text(t("伝票番号")+`： ${slipNumber}`, orderNumberX, 48);

  // 請求先の情報
  const reserveZipCode = reserveData.data.bookingData.applicant?.address?.zipCode ?? "";
  const basicInfoAddress = reserveData.data.bookingData.applicant?.address?.address1 ?? "" + reserveData.data.bookingData.applicant?.address?.address2 ?? "";
  const reserveAddress = basicInfoAddress;
  const lastName = reserveData.data.bookingData.applicant?.lastName ?? "";
  const firstName = reserveData.data.bookingData.applicant?.firstName ?? "";
  const name = lastName + " " + firstName;

  pdfDoc.setFontSize(10);
  pdfDoc.text("〒"+reserveZipCode, leftPositionContent, 48); // 郵便番号
  pdfDoc.text(reserveAddress, leftPositionContent, 54); // 住所
  pdfDoc.setFontSize(14);
  const nameText = name + "　 "+ t("御中")
  pdfDoc.text(nameText, leftPositionContent, 66); // 会社名

  // 請求・請求テキスト

  const text1 = pdfType === "invoice" ? t("下記の通りご請求申し上げます。") : t("下記の金額を領収いたしました。");
  pdfDoc.setLineWidth(0.5);
  pdfDoc.line(leftPosition, 68, 95, 68);
  pdfDoc.setFontSize(11);

  const text1MaxWidth = lang === 'en' ? 90 : 100;
  const text1TextWidth = pdfDoc.getTextWidth(text1);

  if (text1TextWidth < text1MaxWidth) {
    pdfDoc.text(text1, leftPositionContent, 85);
  } else {
    let text1Y = 81;
    const wrapText1 = pdfDoc.splitTextToSize(text1, text1MaxWidth);
    wrapText1.forEach(function(text: string) {
      pdfDoc.text(text, leftPositionContent, text1Y);
      text1Y += 4;
    });
  }

  // 請求・領収ラベル
  const invoicePriceLabel = pdfType === "invoice" ? t("ご請求金額") : t("ご領収金額");
  pdfDoc.line(leftPosition, 100, 114, 100);
  pdfDoc.setTextColor(themeColor);
  pdfDoc.setFontSize(12);
  pdfDoc.text(invoicePriceLabel, leftPositionContent + 9, 108);
  const textWidth = Math.ceil(pdfDoc.getTextWidth(invoicePriceLabel)) + 9;

  // 請求・領収金額
  pdfDoc.setFontSize(16);
  pdfDoc.setTextColor("#000000");
  const totalPrice = formatToPrice(reserveData?.data.bookingData?.travelPrice?.totalPrice);
  pdfDoc.text(`¥${totalPrice}`, leftPositionContent + 9 + textWidth, 109);
  pdfDoc.line(leftPosition, 113, 114, 113);

  // 振込先の情報
  const billingInfoX = 115;
  pdfDoc.setFontSize(11);
  const companyName = t("沖縄ツーリスト株式会社");
  const companyNameWidth = pdfDoc.getTextWidth(companyName);
  const companyNameMaxWidth = 72;
  // 会社名
  if (companyNameWidth < companyNameMaxWidth) {
    pdfDoc.text(companyName, billingInfoX, 64);
  } else {
    // テキストを折り返し表示する
    let companyNameY = 60;
    const wrapText = pdfDoc.splitTextToSize(companyName, companyNameMaxWidth);
    wrapText.forEach(function(text: string) {
      pdfDoc.text(text, billingInfoX, companyNameY);
      companyNameY += 5; // 改行間隔を調整
    });
  }

  pdfDoc.setFontSize(10);
  pdfDoc.text("900-8602", billingInfoX, 74); // 郵便番号
  pdfDoc.text(t("沖縄県那覇市松尾1丁目2番3号"), billingInfoX, 80); // 住所1
  pdfDoc.text(t("電話番号")+":098-917-2593", billingInfoX, 86); // 電話番号
  pdfDoc.text(t("登録番号")+":T7360001000428", billingInfoX, 92); // インボイス登録番号


  // 領収明細
  if (pdfType === "receipt") {
    pdfDoc.setFontSize(9);
    pdfDoc.setTextColor("#000000");
    const receiptDetailsText = t("領収明細");
    const departureText = lang === 'en' ? `（${t("（〇〇出発）")}${departureDate}）` : `（${departureDate}出発）`
    pdfDoc.text(receiptDetailsText+departureText , leftPositionContent, 119);
  }


  // テーブル
  pdfDoc.setLineWidth(0.25);
  pdfDoc.line(leftPosition, 120, leftPosition + 170, 120);
  pdfDoc.setFillColor("#daeef5");
  pdfDoc.rect(leftPosition, 120, 170, 7, "F");
  pdfDoc.setFontSize(10);
  pdfDoc.setTextColor(themeColor);
  const productNameX = lang === 'en' ? leftPositionContent + 40 : leftPositionContent + 45;
  const unitCostX = lang === 'en' ? leftPositionContent + 100 : leftPositionContent + 105;
  const quantityX = lang === 'en' ? leftPositionContent + 125 : leftPositionContent + 130;
  const amountX = lang === 'en' ? leftPositionContent + 148 : leftPositionContent + 153;
  pdfDoc.text(t("商品名"), productNameX, 125);
  pdfDoc.text(t("単価"), unitCostX, 125);
  pdfDoc.text(t("件数"), quantityX, 125);
  pdfDoc.text(t("金額"), amountX, 125);

  let taxableAmount = 0;
  let taxFreePrice = 0;
  let cancelFee = 0;

  // 基本旅行代金
  pdfDoc.setFontSize(9);
  pdfDoc.setTextColor("#000000");
  pdfDoc.text(t("基本旅行代金")+`${toCancelFeeCaption(reserveData?.data.bookingData.status)}`, leftPositionContent, 132); // 商品名
  const basicTravel = reserveData?.data.bookingData.travelPrice?.basicTravel ?? 0;
  let basicTravelTax = 0;
  if (reserveData?.data.bookingData?.status === "cancel") {
    cancelFee += Number(basicTravel);
  } else {
    basicTravelTax += consumptionTax(basicTravel) ?? 0;
    taxableAmount += Number(basicTravel);
  }
  const unitCost = formatToPrice(basicTravel);
  const textWidthUnitCost = pdfDoc.getTextWidth(`${unitCost}`);
  pdfDoc.text(`${unitCost}`, (leftPositionContent + 120) - textWidthUnitCost, 132); // 単価
  pdfDoc.text("1", leftPositionContent + 140, 132); // 件数
  const textPrice = formatToPrice(basicTravel);
  const textWidthPrice = pdfDoc.getTextWidth(`${textPrice}`);
  pdfDoc.text(`${textPrice}`, (leftPositionContent + 167) - textWidthPrice, 132); // 金額

  let yPosition = 132;

  // レンタカー
  let rentalCarTax = 0;
  if (reserveData?.data.subData.rentalCar && reserveData?.data.subData.rentalCar.length) {
    reserveData?.data.subData.rentalCar.map((rentalCar, rIndex) => {
      yPosition = yPosition + 7;
      const rentalCarPrice = rentalCar.data.status === "cancel" ? rentalCar.data.cancelPrice ?? 0 : rentalCar.data.total_price ?? 0;
      if (rentalCar.data.status === "cancel") {
        cancelFee += Number(rentalCarPrice);
      } else {
        rentalCarTax += consumptionTax(rentalCarPrice) ?? 0;
        taxableAmount += Number(rentalCarPrice);
      }
      pdfDoc.text(t("レンタカー")+`${ toCircled(rentalCar.data.order != null ? rentalCar.data.order : rIndex + 1) }${ toCancelFeeCaption(rentalCar.data.status) }`, leftPositionContent, yPosition); // 商品名
      const unitCost2 = formatToPrice(rentalCarPrice);
      const textWidthUnitCost2 = pdfDoc.getTextWidth(`${unitCost2}`);
      pdfDoc.text(`${unitCost2}`, (leftPositionContent + 120) - textWidthUnitCost2, yPosition); // 単価
      pdfDoc.text("1", leftPositionContent + 140, yPosition); // 件数
      const textPrice2 = formatToPrice(rentalCarPrice);
      const textWidthPrice2 = pdfDoc.getTextWidth(`${textPrice2}`);
      pdfDoc.text(`${textPrice2}`, (leftPositionContent + 167) - textWidthPrice2, yPosition); // 金額
    });
  }

  // 追加オプション
  let optionTax = 0;
  if (reserveData?.data.subData.options && reserveData.data.subData.options.length > 0) {
    reserveData.data.subData.options.map((option, oIndex) => {
      yPosition = yPosition + 7;
      const optionPrice = option.data.status === "cancel" ? option.data.cancelPrice ?? 0 : option.data.total_price ?? 0;
      if (option.data.status === "cancel") {
        cancelFee += Number(optionPrice);
      } else {
        optionTax += consumptionTax(optionPrice) ?? 0;
        taxableAmount += Number(optionPrice);
      }
      pdfDoc.text(t("追加オプション")+`${ toCircled(option.data.order != null ? option.data.order : oIndex + 1) }${ toCancelFeeCaption(option.data.status) }`, leftPositionContent, yPosition); // 商品名
      const unitCost2 = formatToPrice(optionPrice);
      const textWidthUnitCost2 = pdfDoc.getTextWidth(`${unitCost2}`);
      pdfDoc.text(`${unitCost2}`, (leftPositionContent + 120) - textWidthUnitCost2, yPosition); // 単価
      pdfDoc.text("1", leftPositionContent + 140, yPosition); // 件数
      const textPrice2 = formatToPrice(optionPrice);
      const textWidthPrice2 = pdfDoc.getTextWidth(`${textPrice2}`);
      pdfDoc.text(`${textPrice2}`, (leftPositionContent + 167) - textWidthPrice2, yPosition); // 金額
    });
  }

  // 国内旅行傷害保険
  if (reserveData?.data.bookingData.travelPrice?.travelInsurance && reserveData?.data.bookingData.travelPrice?.travelInsurance > 0) {
    yPosition = yPosition + 7;
    const travelInsurance = reserveData?.data.bookingData.travelPrice?.travelInsurance ?? 0;
    taxFreePrice += travelInsurance;
    pdfDoc.text(`国内旅行傷害保険`, leftPositionContent, yPosition);
    const unitTravelInsuranceCost = formatToPrice(travelInsurance);
    const textWidthUnitTravelInsuranceCost = pdfDoc.getTextWidth(`${unitTravelInsuranceCost}`);
    pdfDoc.text(`${unitTravelInsuranceCost}`, (leftPositionContent + 120) - textWidthUnitTravelInsuranceCost, yPosition); // 単価
    pdfDoc.text("1", leftPositionContent + 140, yPosition); // 件数
    const textTravelInsurance = formatToPrice(travelInsurance);
    const textWidthTravelInsurance = pdfDoc.getTextWidth(`${textTravelInsurance}`);
    pdfDoc.text(`${textTravelInsurance}`, (leftPositionContent + 167) - textWidthTravelInsurance, yPosition); // 金額
  }

  // 旅のキャンセル保険
  if (reserveData?.data.bookingData?.travelPrice?.cancelInsurance && reserveData?.data.bookingData?.travelPrice?.cancelInsurance > 0) {
    yPosition = yPosition + 7;
    const cancelInsurance = reserveData?.data.bookingData.travelPrice?.cancelInsurance ?? 0;
    taxFreePrice += cancelInsurance;
    pdfDoc.text(`旅のキャンセル保険`, leftPositionContent, yPosition);
    const unitCancelInsuranceCost = formatToPrice(cancelInsurance);
    const textWidthUnitCancelInsuranceCost = pdfDoc.getTextWidth(`${unitCancelInsuranceCost}`);
    pdfDoc.text(`${unitCancelInsuranceCost}`, (leftPositionContent + 120) - textWidthUnitCancelInsuranceCost, yPosition); // 単価
    pdfDoc.text("1", leftPositionContent + 140, yPosition); // 件数
    const textCancelInsurance = formatToPrice(cancelInsurance);
    const textWidthCancelInsurance= pdfDoc.getTextWidth(`${textCancelInsurance}`);
    pdfDoc.text(`${textCancelInsurance}`, (leftPositionContent + 167) - textWidthCancelInsurance, yPosition); // 金額
  }

  // クーポン
  if (reserveData?.data.bookingData?.couponCode) {
    yPosition = yPosition + 7;
    const couponDiscountPrice = reserveData?.data.bookingData?.couponDiscountPrice ?? 0;
    pdfDoc.text(`${reserveData?.data.bookingData?.couponName}`, leftPositionContent, yPosition);
    const unitCouponDiscountPriceCost = "-" + formatToPrice(couponDiscountPrice);
    const textWidthUnitCouponDiscountPriceCost = pdfDoc.getTextWidth(`${unitCouponDiscountPriceCost}`);
    pdfDoc.text(`${unitCouponDiscountPriceCost}`, (leftPositionContent + 120) - textWidthUnitCouponDiscountPriceCost, yPosition); // 単価
    pdfDoc.text("1", leftPositionContent + 140, yPosition); // 件数
    const textCouponDiscountPrice = "-" + formatToPrice(couponDiscountPrice);
    const textWidthCouponDiscountPrice= pdfDoc.getTextWidth(`${textCouponDiscountPrice}`);
    pdfDoc.text(`${textCouponDiscountPrice}`, (leftPositionContent + 167) - textWidthCouponDiscountPrice, yPosition); // 金額

  }


  // 罫線
  for (let i = 0; i <= 14; i++) {
    const lineY = 120 + (7 * i);
    if (i < 2 || i === 14) {
      pdfDoc.line(leftPosition, lineY, leftPosition + 170, lineY);
    } else {
      // 点線を手動で描画
      const dashLength = 0.1; // 点の長さ
      const spaceLength = 0.1; // 点と点の間隔
      const x1 = leftPosition;
      const y1 = lineY;
      const x2 = leftPosition + 170;
      const y2 = lineY;
      let drawDash = true;
      for (let x = x1; x < x2; x += dashLength + spaceLength) {
        if (drawDash) {
          pdfDoc.line(x, y1, x + dashLength, y2);
        }
        drawDash = !drawDash;
      }
    }
  }

  // 外線
  pdfDoc.line(leftPosition, 120, leftPosition, 218);
  pdfDoc.line(billingInfoX, 120, billingInfoX, 127);
  pdfDoc.line(billingInfoX + 28, 120, billingInfoX + 28, 127);
  pdfDoc.line(billingInfoX + 50, 120, billingInfoX + 50, 127);
  pdfDoc.line(leftPosition + 170, 120, leftPosition + 170, 218);
  // 点線
  const lineXList = [billingInfoX, billingInfoX + 28, billingInfoX + 50];
  for (let i = 0; i < lineXList.length; i++) {
    const startX = lineXList[i]; // 線の開始X座標
    const startY = 120; // 線の開始Y座標
    const endY = 218; // 線の終了Y座標
    const dashLength = 0.1; // 点の長さ
    const spaceLength = 0.1; // 点と点の間隔
    const numDashes = (endY - startY) / (dashLength + spaceLength);

    for (let i = 0; i < numDashes; i++) {
      const y1 = startY + i * (dashLength + spaceLength);
      const y2 = y1 + dashLength;
      pdfDoc.line(startX, y1, startX, y2);
    }
  }


  // 金額の情報
  pdfDoc.setLineWidth(0.5);
  pdfDoc.line(billingInfoX, 233, leftPosition + 170, 233);
  pdfDoc.line(billingInfoX, 243, leftPosition + 170, 243);
  pdfDoc.line(billingInfoX, 253, leftPosition + 170, 253);
  pdfDoc.setFontSize(10);
  pdfDoc.setTextColor(themeColor);
  pdfDoc.text(t("課税対象額"), billingInfoX + 1, 230);
  pdfDoc.text(t("消費税(うち10%)"), billingInfoX + 1, 239);
  pdfDoc.text(t("非課税対象額"), billingInfoX + 1, 249);
  pdfDoc.text(t("合計金額"), billingInfoX + 1, 259);
  pdfDoc.setTextColor("#000000");
  // 課税対象額
  pdfDoc.setFontSize(11);
  pdfDoc.text("¥", billingInfoX + 45, 230);
  const subPriceMarginX = pdfDoc.getTextWidth(`${formatToPrice(taxableAmount)}`);
  pdfDoc.text(`${formatToPrice(taxableAmount)}`, (billingInfoX + 73) - subPriceMarginX, 230);

  // 消費税
  const taxPrice = basicTravelTax + rentalCarTax + optionTax;
  pdfDoc.text("¥", billingInfoX + 45, 239);
  const tax = formatToPrice(taxPrice);
  const taxMarginX = pdfDoc.getTextWidth(`${tax}`);
  pdfDoc.text(`${tax}`, (billingInfoX + 73) - taxMarginX, 239);

  // 非課税対象額
  pdfDoc.setFontSize(12);
  // 他の3行と同じ位置に揃える（+30 だと英語ラベル "Non-taxable Amount" と重なる）
  pdfDoc.text("¥", billingInfoX + 45, 249);
  const taxFreePriceMarginX = pdfDoc.getTextWidth(`${formatToPrice(taxFreePrice + cancelFee)}`);
  pdfDoc.text(`${formatToPrice(taxFreePrice + cancelFee)}`, (billingInfoX + 73) - taxFreePriceMarginX, 249);

  // 合計金額
  pdfDoc.setFontSize(12);
  pdfDoc.text("¥", billingInfoX + 45, 259);
  const totalPriceMarginX = pdfDoc.getTextWidth(`${totalPrice}`);
  pdfDoc.text(`${totalPrice}`, (billingInfoX + 73) - totalPriceMarginX, 259);


  // 備考欄
  const note = "";
  pdfDoc.setTextColor(themeColor);
  pdfDoc.text(t("備考"), leftPositionContent, 265);

  // テキストを折り返し表示する
  let noteY = 270;
  let noteX = leftPosition + 150;
  pdfDoc.setTextColor("#000000");
  pdfDoc.setFontSize(10);

  if (pdfType === "receipt") {
    const receiptNote = t("オンラインクレジットカード決済にてお支払い完了");
    pdfDoc.text(receiptNote, leftPositionContent, noteY);
    noteY += 4; // 改行間隔を調整
  } else {
    const receiptNote = lang === 'en' ? t("（〇〇出発）")+`${departureDate}` :`${departureDate}出発`;
    pdfDoc.text(receiptNote, leftPositionContent, noteY);
    noteY += 4; // 改行間隔を調整
  }

  let wrapText = pdfDoc.splitTextToSize(note, noteX);
  if (2 <= wrapText.length) {
    wrapText = pdfDoc.splitTextToSize(note, noteX);
  }
  wrapText.forEach(function(text: string) {
    pdfDoc.text(text, leftPositionContent, noteY);
    noteY += 4; // 改行間隔を調整
  });

  pdfDoc.line(leftPosition, 280, leftPosition + 170, 280);
}
